import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { formatCampaignMoney } from '../components/ContactAgentMetricCard.utils';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';
import type {
  ContactConversationThread,
  ContactExceptionMessage,
  ContactTrackingResult,
} from '@workspace/types';

const FSM_LABELS: Record<string, string> = {
  PENDIENTE_CONTACTO: 'Pendiente de contacto',
  MENSAJE_ENVIADO: 'Mensaje enviado',
  SIN_RESPUESTA: 'Sin respuesta',
  REINTENTO_1: 'Reintento 1',
  REINTENTO_2: 'Reintento 2',
  AGOTADO: 'Agotado',
  RESPONDIDO: 'Respondio',
  CONSULTA: 'Consulta',
  OBJECION: 'Negociando',
  FACTURA_SOLICITADA: 'Factura solicitada',
  MEDIO_PAGO_SOLICITADO: 'Medios de pago solicitados',
  PROMESA_PAGO: 'Compromiso de pago',
  PROMESA_CUMPLIDA: 'Compromiso cumplido',
  PROMESA_VENCIDA: 'Compromiso vencido',
  DISPUTA: 'Disputa',
  PAGADO: 'Pago declarado',
  PAGADO_CONFIRMADO: 'Pago confirmado',
  DERIVADO_HUMANO: 'Derivado a operador',
  IDENTIFICACION: 'Identificacion',
  OPT_OUT: 'Baja',
  NO_CONTACTAR: 'No contactar',
};

const PROMISE_STATUS: Record<string, { label: string; variant: 'secondary' | 'default' | 'destructive' }> = {
  ACTIVE: { label: 'Activo', variant: 'secondary' },
  KEPT: { label: 'Cumplido', variant: 'default' },
  EXPIRED: { label: 'Vencido', variant: 'destructive' },
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  DECLARED: 'Declarado',
  RECEIPT: 'Con comprobante',
  PROBABLE: 'Probable',
  CONFIRMED: 'Confirmado',
  REJECTED: 'Rechazado',
};

const AUDIT_LABELS: Record<string, string> = {
  CAMPAIGN_TEMPLATE: 'Mensaje de campaña enviado',
  FSM_TRANSITION: 'Avance de gestion',
  RETOMAR_VENTANA: 'Recordatorio por silencio',
  PROMESA_RESULTADO: 'Resultado de compromiso informado',
  OPERATOR_REPLY: 'Respuesta de operador',
  OPERATOR_INTERVENED: 'Operador intervino el chat',
  PAYMENT_CONFIRMED: 'Pago confirmado',
  PAYMENT_REJECTED: 'Pago rechazado',
  EXCEPTION_CLOSED: 'Excepcion cerrada',
  OPT_OUT: 'Baja',
  OPT_OUT_REVOKED: 'Baja revocada',
  CONTACT_UPDATED: 'Datos actualizados',
};

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

function fsmLabel(state: string | null): string {
  if (!state) {
    return '—';
  }
  return FSM_LABELS[state] ?? state;
}

function messageAuthor(message: ContactExceptionMessage): string {
  if (message.contextContent === 'OPERATOR') {
    return 'Operador';
  }
  return message.sender === 'USER' ? 'Cliente' : 'Bot';
}

function whatsAppWindowClosed(thread: ContactConversationThread): boolean {
  if (thread.channel !== 'WHATSAPP' || !thread.windowExpiresAt) {
    return false;
  }
  return new Date(thread.windowExpiresAt).getTime() <= Date.now();
}

export function ContactAgentContactTrackingPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const { ready, tenantId } = useContactAgentTenant();
  const [tracking, setTracking] = useState<ContactTrackingResult | null>(null);
  const [thread, setThread] = useState<ContactConversationThread | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadTracking = useCallback(async () => {
    if (!contactId) {
      return;
    }
    const result = await contactAgentApi.getContactTracking(contactId);
    setTracking(result);
    setSelectedConversationId(current => {
      if (current && result.conversations.some(item => item.id === current)) {
        return current;
      }
      return result.conversations[0]?.id ?? '';
    });
  }, [contactId]);

  const loadThread = useCallback(async () => {
    if (!contactId || !selectedConversationId) {
      setThread(null);
      return;
    }
    setThread(await contactAgentApi.getContactThread(contactId, selectedConversationId));
  }, [contactId, selectedConversationId]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    setError('');
    void loadTracking().catch(err => setError(contactAgentError(err)));
  }, [ready, tenantId, loadTracking]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    void loadThread().catch(err => setError(contactAgentError(err)));
  }, [ready, loadThread]);

  const refreshAll = async () => {
    await Promise.all([loadTracking(), loadThread()]);
  };

  const runAction = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await action();
      await refreshAll();
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setBusy(false);
    }
  };

  const intervene = () =>
    runAction(async () => {
      if (!contactId) {
        return;
      }
      await contactAgentApi.interveneContact(contactId, selectedConversationId || undefined);
    });

  const sendReply = () =>
    runAction(async () => {
      if (!thread?.openExceptionId || !reply.trim()) {
        return;
      }
      await contactAgentApi.replyException(thread.openExceptionId, reply.trim());
      setReply('');
    });

  const returnToBot = () =>
    runAction(async () => {
      if (!thread?.openExceptionId) {
        return;
      }
      await contactAgentApi.closeException(thread.openExceptionId, true);
    });

  const confirmPayment = (paymentId: string) =>
    runAction(async () => {
      if (!thread?.openExceptionId) {
        return;
      }
      await contactAgentApi.confirmExceptionPayment(thread.openExceptionId, paymentId);
    });

  const rejectPayment = (paymentId: string) =>
    runAction(async () => {
      if (!thread?.openExceptionId) {
        return;
      }
      const reason = window.prompt('Motivo del rechazo (opcional):') ?? undefined;
      await contactAgentApi.rejectExceptionPayment(thread.openExceptionId, paymentId, reason);
    });

  const contact = tracking?.contact;
  const title = contact
    ? contact.businessName || contact.firstName || contact.phoneE164 || 'Contacto'
    : 'Ficha de contacto';
  const canOperate = Boolean(
    thread && thread.openExceptionId && thread.handoffStatus === 'HUMAN_ACTIVE',
  );

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title={title}
        description='Seguimiento completo de la deuda: conversación, saldos, compromisos e historial.'
        toolbar={
          <div className='flex flex-wrap items-center gap-2'>
            {contact ? (
              <>
                <span className='font-mono text-sm'>{contact.phoneE164 ?? '—'}</span>
                {contact.optOut ? (
                  <Badge variant='destructive'>Baja</Badge>
                ) : (
                  <Badge variant='secondary'>Contactable</Badge>
                )}
              </>
            ) : null}
            <Button asChild variant='outline' size='sm'>
              <Link to='/admin/contact-agent/contacts'>Volver a contactos</Link>
            </Button>
          </div>
        }
      />

      {error ? <p className='text-destructive'>{error}</p> : null}

      {tracking && contact ? (
        <div className='grid gap-4 lg:grid-cols-[1.2fr_1fr]'>
          <div className='space-y-4'>
            <Card className='rounded-2xl shadow-sm'>
              <CardHeader className='space-y-3'>
                <CardTitle className='text-lg'>Conversación</CardTitle>
                {tracking.conversations.length > 1 ? (
                  <div className='flex flex-wrap gap-2'>
                    {tracking.conversations.map(conversation => (
                      <Button
                        key={conversation.id}
                        type='button'
                        size='sm'
                        variant={conversation.id === selectedConversationId ? 'secondary' : 'outline'}
                        onClick={() => setSelectedConversationId(conversation.id)}
                      >
                        {conversation.channel}
                        {conversation.campaignName ? ` · ${conversation.campaignName}` : ''}
                      </Button>
                    ))}
                  </div>
                ) : null}
                {thread ? (
                  <p className='text-xs text-muted-foreground'>
                    Estado: {fsmLabel(thread.fsmState)}
                    {thread.pendingReason ? ` · Pendiente: ${thread.pendingReason}` : ''}
                    {canOperate ? ' · Intervenida por operador' : ''}
                  </p>
                ) : null}
              </CardHeader>
              <CardContent className='space-y-3'>
                {tracking.conversations.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>Sin conversaciones registradas.</p>
                ) : null}
                {thread ? (
                  <div className='max-h-96 space-y-2 overflow-y-auto rounded-xl border p-3'>
                    {thread.messages.length === 0 ? (
                      <p className='text-sm text-muted-foreground'>Sin mensajes aún.</p>
                    ) : (
                      thread.messages.map(message => (
                        <div key={message.id} className='rounded-lg bg-muted/40 p-2 text-sm'>
                          <p className='text-xs text-muted-foreground'>
                            {messageAuthor(message)} · {formatDateTime(message.createdAt)}
                          </p>
                          <p className='whitespace-pre-wrap'>{message.content}</p>
                          {message.fileUrl ? (
                            <a
                              className='text-xs underline'
                              href={message.fileUrl}
                              target='_blank'
                              rel='noreferrer'
                            >
                              Ver adjunto
                            </a>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                ) : null}

                {thread && canOperate ? (
                  <>
                    {whatsAppWindowClosed(thread) ? (
                      <p className='text-sm text-amber-700'>
                        La ventana de 24 h de WhatsApp está cerrada: Meta puede rechazar el envío.
                        Usá una plantilla o esperá un mensaje del cliente.
                      </p>
                    ) : null}
                    <Textarea
                      rows={3}
                      maxLength={4096}
                      value={reply}
                      onChange={event => setReply(event.target.value)}
                      placeholder='Respuesta al cliente por el canal del hilo'
                    />
                    <div className='flex flex-wrap gap-2'>
                      <Button disabled={busy || !reply.trim()} onClick={() => void sendReply()}>
                        Enviar
                      </Button>
                      <Button variant='outline' disabled={busy} onClick={() => void returnToBot()}>
                        Devolver al bot
                      </Button>
                      <Button variant='ghost' size='sm' disabled={busy} onClick={() => void refreshAll()}>
                        Actualizar
                      </Button>
                    </div>
                  </>
                ) : null}

                {thread && !canOperate ? (
                  <div className='flex flex-wrap items-center gap-3'>
                    <Button disabled={busy} onClick={() => void intervene()}>
                      Intervenir
                    </Button>
                    <p className='text-xs text-muted-foreground'>
                      Silencia al bot, abre un caso auditado y habilita el chat. Al terminar,
                      &ldquo;Devolver al bot&rdquo; retoma la gestión automática.
                    </p>
                    <Button variant='ghost' size='sm' disabled={busy} onClick={() => void refreshAll()}>
                      Actualizar
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card className='rounded-2xl shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg'>Pagos</CardTitle>
              </CardHeader>
              <CardContent className='space-y-2'>
                {tracking.payments.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>Sin pagos registrados.</p>
                ) : (
                  tracking.payments.map(payment => (
                    <div
                      key={payment.id}
                      className='flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm'
                    >
                      <span>
                        {payment.debt?.invoiceNumber ?? 'Pago'} ·{' '}
                        {PAYMENT_STATUS_LABELS[payment.status] ?? payment.status} ·{' '}
                        {formatCampaignMoney(payment.amount)}
                        {payment.evidenceRef && payment.evidenceRef.startsWith('http') ? (
                          <>
                            {' '}
                            <a
                              className='underline'
                              href={payment.evidenceRef}
                              target='_blank'
                              rel='noreferrer'
                            >
                              comprobante
                            </a>
                          </>
                        ) : null}
                      </span>
                      {payment.status !== 'CONFIRMED' && payment.status !== 'REJECTED' && canOperate ? (
                        <div className='flex items-center gap-2'>
                          <Button size='sm' disabled={busy} onClick={() => void confirmPayment(payment.id)}>
                            Confirmar
                          </Button>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={busy}
                            onClick={() => void rejectPayment(payment.id)}
                          >
                            Rechazar
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
                {tracking.payments.some(
                  payment => payment.status !== 'CONFIRMED' && payment.status !== 'REJECTED',
                ) && !canOperate ? (
                  <p className='text-xs text-muted-foreground'>
                    Para confirmar un pago primero intervení la conversación.
                  </p>
                ) : null}
              </CardContent>
            </Card>
          </div>

          <div className='space-y-4'>
            <Card className='rounded-2xl shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg'>Deudas con saldo</CardTitle>
              </CardHeader>
              <CardContent>
                {contact.debts && contact.debts.length > 0 ? (
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b text-left text-muted-foreground'>
                        <th className='py-2 pr-4'>Factura</th>
                        <th className='py-2 pr-4'>Saldo</th>
                        <th className='py-2'>Vencimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contact.debts.map(debt => (
                        <tr key={debt.id} className='border-b last:border-0'>
                          <td className='py-2 pr-4 font-mono'>{debt.invoiceNumber}</td>
                          <td className='py-2 pr-4'>{formatCampaignMoney(debt.balance)}</td>
                          <td className='py-2'>{debt.dueDate.slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className='text-sm text-muted-foreground'>Sin deudas con saldo pendiente.</p>
                )}
              </CardContent>
            </Card>

            <Card className='rounded-2xl shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg'>Compromisos de pago</CardTitle>
              </CardHeader>
              <CardContent>
                {tracking.promises.length > 0 ? (
                  <div className='space-y-2'>
                    {tracking.promises.map(promise => {
                      const status = PROMISE_STATUS[promise.status] ?? {
                        label: promise.status,
                        variant: 'secondary' as const,
                      };
                      return (
                        <div
                          key={promise.id}
                          className='flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm'
                        >
                          <span>
                            {promise.invoiceNumber} · {formatCampaignMoney(promise.amount)} · para el{' '}
                            {promise.promisedDate}
                          </span>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className='text-sm text-muted-foreground'>Todavia no hay compromisos registrados.</p>
                )}
              </CardContent>
            </Card>

            <Card className='rounded-2xl shadow-sm'>
              <CardHeader>
                <CardTitle className='text-lg'>Historial de gestion</CardTitle>
              </CardHeader>
              <CardContent>
                {tracking.audits.length > 0 ? (
                  <ul className='space-y-2'>
                    {tracking.audits.map(audit => (
                      <li
                        key={audit.id}
                        className='flex flex-wrap items-baseline gap-x-3 border-b pb-2 text-sm last:border-0'
                      >
                        <span className='text-xs text-muted-foreground'>
                          {formatDateTime(audit.createdAt)}
                        </span>
                        <span>{AUDIT_LABELS[audit.action] ?? audit.action}</span>
                        <span className='text-xs text-muted-foreground'>
                          {audit.actor === 'HUMAN' ? 'Operador' : audit.actor === 'AGENT' ? 'Bot' : 'Sistema'}
                          {audit.template ? ` · ${audit.template}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className='text-sm text-muted-foreground'>Sin eventos registrados.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : !error ? (
        <p className='text-sm text-muted-foreground'>Cargando ficha…</p>
      ) : null}
    </ContactAgentPage>
  );
}
